import type { Credentials } from "../api/client.js";

/**
 * Where the signed-in session lives.
 *
 * The token is a bearer credential, so this is a considered trade-off rather
 * than a default: it is kept in `localStorage` so a reload does not sign the
 * user out, which for software somebody has open all day is the difference
 * between usable and infuriating. That choice is only defensible because the
 * API issues short-lived, individually revocable session tokens
 * (`SESSION_TTL_DAYS`, `app_revoke_session`) rather than a long-lived JWT, and
 * because the portal never renders untrusted HTML. If either changes, this
 * should move to a `HttpOnly` cookie and the API should learn to set one.
 */

export interface StoredSession {
  readonly token: string;
  readonly expiresAt: string;
  /** Echoed back on screen so it is obvious which account is signed in. */
  readonly email: string;
  /**
   * The organisation this session is acting as, or null when the user belongs
   * to exactly one and the API can resolve it alone.
   */
  readonly organisationId: string | null;
}

/** Why the session ended, so the sign-in screen can say something useful. */
export type EndedReason = string | null;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const KEY = "sts.portal.session";

export class SessionStore {
  private readonly storage: StorageLike;
  private readonly listeners = new Set<() => void>();
  /**
   * Cached so `getSnapshot` returns a stable reference. `useSyncExternalStore`
   * re-renders forever if the snapshot is a fresh object every call.
   */
  private current: StoredSession | null;
  private ended: EndedReason = null;

  constructor(storage: StorageLike = memoryStorage()) {
    this.storage = storage;
    this.current = this.read();
  }

  /** The live session, or null. Expiry is checked here, not only by the API. */
  get(): StoredSession | null {
    return this.current;
  }

  /** Credentials in the shape the API client wants, or null when signed out. */
  credentials(): Credentials | null {
    const session = this.current;
    if (!session) return null;
    return { token: session.token, organisationId: session.organisationId };
  }

  set(session: StoredSession): void {
    this.current = session;
    this.ended = null;
    this.storage.setItem(KEY, JSON.stringify(session));
    this.emit();
  }

  /**
   * Sign out. `reason` is the API's own message when the session was refused,
   * so the sign-in screen can explain rather than just reappearing — the "you
   * belong to more than one organisation" case is otherwise baffling.
   */
  clear(reason: EndedReason = null): void {
    this.current = null;
    this.ended = reason;
    this.storage.removeItem(KEY);
    this.emit();
  }

  endedReason(): EndedReason {
    return this.ended;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): StoredSession | null => this.current;

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private read(): StoredSession | null {
    const raw = this.storage.getItem(KEY);
    if (!raw) return null;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw) as unknown;
    } catch {
      // Corrupt or from an older version of this code. Dropping it is safe:
      // the worst case is one sign-in.
      this.storage.removeItem(KEY);
      return null;
    }

    const session = parsed as Partial<StoredSession> | null;
    if (
      !session ||
      typeof session.token !== "string" ||
      typeof session.expiresAt !== "string"
    ) {
      this.storage.removeItem(KEY);
      return null;
    }

    // An expired token would otherwise produce a 401 on the first request of
    // every screen. Checking it here means the user lands on sign-in instead.
    if (Date.parse(session.expiresAt) <= Date.now()) {
      this.storage.removeItem(KEY);
      return null;
    }

    return {
      token: session.token,
      expiresAt: session.expiresAt,
      email: typeof session.email === "string" ? session.email : "",
      organisationId:
        typeof session.organisationId === "string"
          ? session.organisationId
          : null,
    };
  }
}

/** An in-memory stand-in, used in tests and if `localStorage` is unavailable. */
export function memoryStorage(): StorageLike {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
}

/**
 * `localStorage` when the browser allows it.
 *
 * Safari in private browsing and any page with storage blocked throw on access
 * rather than returning null, and an exception here would take the whole app
 * down before it rendered anything.
 */
export function browserStorage(): StorageLike {
  try {
    const probe = "sts.portal.probe";
    globalThis.localStorage.setItem(probe, "1");
    globalThis.localStorage.removeItem(probe);
    return globalThis.localStorage;
  } catch {
    return memoryStorage();
  }
}
